import React, { useState } from 'react'
import { Box, BoxProps, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

export interface PanelSectionProps extends Omit<BoxProps, 'title' | 'children'> {
  title: string
  /** Optional element rendered right of the title (e.g. a value chip) */
  trailing?: React.ReactNode
  /** Uncontrolled initial state. Ignored if `open` is provided. */
  defaultOpen?: boolean
  /** Controlled open state */
  open?: boolean
  onToggle?: (open: boolean) => void
  children: React.ReactNode
}

const Root = styled(Box)({
  borderBottom: '1px solid var(--ds-border)',
  '&:last-of-type': { borderBottom: 'none' },
})

const Header = styled('button')({
  all: 'unset',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  boxSizing: 'border-box',
  cursor: 'pointer',
  padding: 'var(--ds-space-4) 0',
  gap: 'var(--ds-space-2)',
  '&:focus-visible': {
    outline: '2px solid var(--ds-ink)',
    outlineOffset: 2,
  },
})

const HeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--ds-space-2)',
  minWidth: 0,
})

const Title = styled(Typography)({
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: 'var(--ds-ink)',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const Chevron = styled(ExpandMoreIcon, {
  shouldForwardProp: (p) => p !== '$open',
})<{ $open?: boolean }>(({ $open }) => ({
  fontSize: 18,
  color: 'var(--ds-ink-subtle)',
  transform: $open ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: `transform var(--ds-duration-normal) var(--ds-ease)`,
  flexShrink: 0,
}))

const Content = styled(Box, {
  shouldForwardProp: (p) => p !== '$open',
})<{ $open?: boolean }>(({ $open }) => ({
  display: $open ? 'block' : 'none',
  paddingBottom: 'var(--ds-space-4)',
}))

/** Collapsible labelled group — studio property-panel sections */
export function PanelSection({
  title,
  trailing,
  defaultOpen = true,
  open,
  onToggle,
  children,
  ...rest
}: PanelSectionProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isOpen = open ?? internalOpen

  const toggle = () => {
    const next = !isOpen
    if (open === undefined) setInternalOpen(next)
    onToggle?.(next)
  }

  return (
    <Root {...rest}>
      <Header type='button' aria-expanded={isOpen} onClick={toggle}>
        <HeaderLeft>
          <Title>{title}</Title>
        </HeaderLeft>
        <Box display='flex' alignItems='center' gap='var(--ds-space-2)'>
          {trailing}
          <Chevron $open={isOpen} />
        </Box>
      </Header>
      <Content $open={isOpen}>{children}</Content>
    </Root>
  )
}
